import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import Tooltip from '../ui/Tooltip';
import Spinner from '../ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { resolveDisplayName } from '../../lib/usersDirectory';
import { htmlToPlainText } from '../../lib/emailCompose';

const { FiMail, FiEye, FiMousePointer } = FiIcons;

const OPEN_INFLATION_NOTE =
  'Some email apps (like Apple Mail) open emails automatically, so this can be inflated.';

const STATUS_META = {
  sent: { label: 'Sent', className: 'bg-skyTint text-navy' },
  delivered: { label: 'Delivered', className: 'bg-primary/15 text-primary-dark' },
  opened: { label: 'Opened', className: 'bg-successBg text-success' },
  clicked: { label: 'Clicked', className: 'bg-primary text-white' },
  bounced: { label: 'Bounced', className: 'bg-red-100 text-red-700' },
  spam: { label: 'Spam', className: 'bg-red-100 text-red-700' },
  unsubscribed: { label: 'Unsubscribed', className: 'bg-warningBg text-warning' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
  sent_gmail: { label: 'Sent via Gmail', className: 'bg-lockedBg text-locked' },
};

const GMAIL_TRACKING_NOTE = "Gmail open tracking is approximate, and clicks aren't tracked.";

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmtFull = (d) => format(d, 'MMM d, yyyy · h:mm a');
const ago = (d) => formatDistanceToNow(d, { addSuffix: true });

function statusTooltip(log, status) {
  const lines = [];
  const add = (label, value) => {
    const d = toDate(value);
    if (d) lines.push(`${label}: ${fmtFull(d)}`);
  };
  if (log.send_method === 'gmail_manual') {
    if (!log.tracking_token) {
      return "Sent manually from Gmail before open tracking was added, so opens and clicks aren't tracked.";
    }
    lines.push('Sent manually from Gmail.');
    add('First opened', log.first_opened_at);
    add('Last opened', log.last_opened_at);
    lines.push('', GMAIL_TRACKING_NOTE);
    return lines.join('\n');
  }
  add('Delivered', log.delivered_at);
  add('First opened', log.first_opened_at);
  add('Last opened', log.last_opened_at);
  add('First clicked', log.first_clicked_at);
  add('Last clicked', log.last_clicked_at);
  add('Bounced', log.bounced_at);
  if (!lines.length && status === 'sent') lines.push('Sent through Brevo. Waiting for delivery confirmation.');
  if (status === 'opened') lines.push('', OPEN_INFLATION_NOTE);
  return lines.join('\n');
}

function engagementParts(log) {
  const parts = [];
  const lastOpened = toDate(log.last_opened_at);
  const lastClicked = toDate(log.last_clicked_at);
  if (log.open_count > 0 || lastOpened) {
    const count = log.open_count > 0 ? ` ${log.open_count}×` : '';
    parts.push({
      icon: FiEye,
      text: `Opened${count}${lastOpened ? `, last ${ago(lastOpened)}` : ''}`,
    });
  }
  if (log.click_count > 0 || lastClicked) {
    const count = log.click_count > 0 ? ` ${log.click_count}×` : '';
    parts.push({
      icon: FiMousePointer,
      text: `Clicked${count}${lastClicked ? `, last ${ago(lastClicked)}` : ''}`,
    });
  }
  return parts;
}

function snippet(html, maxLen = 140) {
  const text = htmlToPlainText(html).replace(/\s+/g, ' ').trim();
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

export default function EmailHistoryList({ logs, loading, emptyHint }) {
  if (loading && !logs.length) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading emails…
      </div>
    );
  }

  if (!logs.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <SafeIcon icon={FiMail} className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm font-medium text-gray-600">No emails sent yet</p>
        {emptyHint ? <p className="text-xs text-gray-400 mt-1 max-w-xs">{emptyHint}</p> : null}
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {logs.map((log) => {
        const status = log.status || 'sent';
        const meta = STATUS_META[status] || { label: status, className: 'bg-skyTint text-navy' };
        const sentAt = toDate(log.created_at);
        const senderName = log.user ? resolveDisplayName(log.agent_id, log.user) : null;
        const engagement = engagementParts(log);
        const preview = snippet(log.body_html);

        return (
          <li key={log.id} className="bg-white px-3.5 py-3 rounded-lg border border-gray-100 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-900 truncate max-w-full">
                    {log.subject || '(no subject)'}
                  </h4>
                  <Tooltip content={statusTooltip(log, status)}>
                    <span
                      className={`inline-flex items-center rounded-[100px] px-2 py-0.5 text-[11px] font-semibold cursor-default ${meta.className}`}
                    >
                      {meta.label}
                    </span>
                  </Tooltip>
                </div>
                {preview ? <p className="text-xs text-gray-600 mt-1 truncate">{preview}</p> : null}
              </div>
              <span
                className="text-[11px] text-gray-400 whitespace-nowrap shrink-0"
                title={sentAt ? ago(sentAt) : undefined}
              >
                {sentAt ? fmtFull(sentAt) : '—'}
              </span>
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
              {engagement.map((part) => (
                <span key={part.text} className="inline-flex items-center gap-1 text-navy/70">
                  <SafeIcon icon={part.icon} className="w-3 h-3 text-primary" />
                  {part.text}
                </span>
              ))}
              {senderName ? <span>Sent by {senderName}</span> : null}
              {log.to_email ? <span className="truncate">To {log.to_email}</span> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
